// ===============================
// UK CPB Script — Price + Klarna + Clearpay (STATIC KLARNA)
// Klarna shows base price only (no dynamic updates) 
// ===============================

window.PXUTheme.contentCreator.accordion = {
  init: function () {
    const $accordionHeading = $(
      ".accordion > dt > a, [data-cc-accordion] > dt > a"
    );
    $(".accordion > dd, [data-cc-accordion] > dd").attr("aria-hidden", true);
    $accordionHeading.attr("aria-expanded", false);
    $accordionHeading.on("click", function () {
      let state = $(this).attr("aria-expanded") === "false" ? true : false;
      $(this).attr("aria-expanded", state);
      $(this).parent().next().attr("aria-hidden", !state);
      return false;
    });
    $accordionHeading.on("keydown", function (event) {
      let keyCode = event.keyCode || event.which;
      if (keyCode === 13) {
        $(this).trigger("activate");
      }
    });
  },
  unload: function () {
    $(".accordion > dt > a, [data-cc-accordion] > dt > a").off("click activate");
    $(".accordion > dt > a, [data-cc-accordion] > dt > a").off("keydown");
  },
};

(function ($) {
  'use strict';

  /* -------------------------------------------
   * Accordion module (delegated, no dbl-toggle)
   * ----------------------------------------- */
  window.PXUTheme.contentCreator.accordion = window.PXUTheme.contentCreator.accordion || {
    _bound: false,
    _handleClick: null,

    init() {
      if (this._bound) return;

      const containers = document.querySelectorAll('.accordion, [data-cc-accordion]');
      if (!containers.length) return;

      containers.forEach(container => {
        container.querySelectorAll('dd').forEach(el => el.setAttribute('aria-hidden', 'true'));
        container.querySelectorAll('dt > a').forEach(el => el.setAttribute('aria-expanded', 'false'));
      });

      this._handleClick = (e) => {
        const target = e.target.closest('.accordion > dt > a, [data-cc-accordion] > dt > a');
        if (!target) return;

        e.preventDefault();

        const isExpanded = target.getAttribute('aria-expanded') === 'true';
        const content = target.parentElement?.nextElementSibling;

        target.setAttribute('aria-expanded', String(!isExpanded));
        if (content) content.setAttribute('aria-hidden', String(isExpanded));
      };

      document.addEventListener('click', this._handleClick);
      this._bound = true;
    },

    unload() {
      if (!this._bound) return;
      document.removeEventListener('click', this._handleClick);
      this._handleClick = null;
      this._bound = false;
    }
  };

  /* -------------------------------------------
   * Main CPB module
   * ----------------------------------------- */
  const CONFIG = {
    DEBUG: false,
    RETRY_DELAYS_MS: [0, 80, 200, 450, 900],
    OOS_RETRY_DELAYS_MS: [150, 500, 1200],

    ENABLE_KLARNA: true,
    ENABLE_AFTERPAY: true,

    KLARNA_FIND_SEL: 'klarna-placement',
    AFTERPAY_DEBOUNCE: 250
  };

  const state = {
    builder: null,
    panelsContainer: null,

    customPricesContainer: null,
    priceObserver: null,
    rafId: null,

    // Klarna
    klarnaWrap: null,
    klarnaInitialized: false,
    klarnaBasePence: null,
    movedKlarnaOnce: false,

    // Clearpay / Afterpay
    afterpayWrap: null,
    updatingAfterpay: false,
    afterpayDebounceTimer: null,
    lastAfterpayAmount: null,

    timeouts: new Set(),
    mountedId: null
  };

  /* -------------------------------------------
   * Utilities
   * ----------------------------------------- */
  const utils = {
    qs: (sel, parent = document) => parent.querySelector(sel),
    qsa: (sel, parent = document) => parent.querySelectorAll(sel),

    log(...args) {
      if (CONFIG.DEBUG) console.log('CPB-UK:', ...args);
    },
    error(...args) {
      console.error('CPB-UK:', ...args);
    },

    setTrackedTimeout(fn, delay) {
      const id = setTimeout(() => {
        state.timeouts.delete(id);
        fn();
      }, delay);
      state.timeouts.add(id);
      return id;
    },

    clearAllTimeouts() {
      state.timeouts.forEach(clearTimeout);
      state.timeouts.clear();
      if (state.afterpayDebounceTimer) {
        clearTimeout(state.afterpayDebounceTimer);
        state.afterpayDebounceTimer = null;
      }
    },

    retry(fn, delays = CONFIG.RETRY_DELAYS_MS) {
      delays.forEach(delay => {
        utils.setTrackedTimeout(() => {
          try { fn(); } catch (_) {}
        }, delay);
      });
    },

    isVisible(el) {
      return !!(el && el.offsetParent !== null);
    },

    newMountId() {
      return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }
  };

  /* -------------------------------------------
   * Cleanup
   * ----------------------------------------- */
  function cleanup() {
    utils.clearAllTimeouts();

    if (state.rafId) {
      cancelAnimationFrame(state.rafId);
      state.rafId = null;
    }

    if (state.priceObserver) {
      state.priceObserver.disconnect();
      state.priceObserver = null;
    }

    window.PXUTheme.contentCreator.accordion?.unload?.();

    state.builder = null;
    state.panelsContainer = null;
    state.customPricesContainer = null;
    state.klarnaWrap = null;
    state.afterpayWrap = null;
    state.klarnaInitialized = false;
    state.klarnaBasePence = null;
    state.movedKlarnaOnce = false;
    state.updatingAfterpay = false;
    state.lastAfterpayAmount = null;
    state.mountedId = null;
  }

  function initGlobals() {
    state.builder = utils.qs('#product-builder');
    if (!state.builder) return false;

    state.panelsContainer = utils.qs('.cpb-panels-container', state.builder);
    return !!state.panelsContainer;
  }

  /* -------------------------------------------
   * Price formatting (£, commas, no decimals)
   * ----------------------------------------- */
  const PRICE_REGEX = /(\d[\d,]*)/;

  function formatPrice(text) {
    const raw = (text || '').trim();
    const match = raw.match(PRICE_REGEX);
    if (!match) return '£0';

    const wholeNumber = match[1].replace(/,/g, '');
    const withCommas = wholeNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return `£${withCommas}`;
  }

  /* -------------------------------------------
   * Price sources (for Klarna/Afterpay)
   * ----------------------------------------- */
  function getPriceFromCPBState() {
    try {
      const st = (typeof CPB?.App === 'function')
        ? CPB.App().getStore().getState()
        : CPB?.App?.getStore?.()?.getState?.();

      const raw = st?.Product?.price;
      const n = Number(raw);
      return Number.isFinite(n) && n > 0 ? n : null;
    } catch {
      return null;
    }
  }

  function getPriceFromDOMFallback() {
    const el = utils.qs('.cpb-product-price', state.panelsContainer)
      || utils.qs('.cpb-custom-product-price');

    if (!el) return null;

    const txt = (el.textContent || '').trim();
    const n = parseFloat(txt.replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  function poundsToPenceInt(pounds) {
    const n = Number(pounds);
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.round(n * 100));
  }

  /* -------------------------------------------
   * Custom price element
   * ----------------------------------------- */
  function findBestPriceEl() {
    const prices = Array.from(utils.qsa('.cpb-product-price', state.panelsContainer));
    if (!prices.length) return null;

    const visible = prices.slice().reverse().find(utils.isVisible);
    return visible || prices[prices.length - 1];
  }

  function ensureCustomPriceContainer() {
    if (state.customPricesContainer) return true;

    const realPrice = findBestPriceEl();
    if (!realPrice) return false;

    const container = document.createElement('div');
    container.className = 'cpb-custom-prices-container';

    const priceWrapper = document.createElement('div');
    priceWrapper.className = 'cpb-custom-product-price-container';

    const customPrice = document.createElement('span');
    customPrice.className = 'cpb-custom-product-price';
    customPrice.textContent = formatPrice(realPrice.textContent);

    priceWrapper.appendChild(customPrice);
    container.appendChild(priceWrapper);

    state.customPricesContainer = container;
    state.builder.dataset.customProductPrice = 'true';

    const updatePrice = () => {
      if (state.rafId) return;
      state.rafId = requestAnimationFrame(() => {
        state.rafId = null;
        const currentPriceEl = findBestPriceEl() || realPrice;
        customPrice.textContent = formatPrice(currentPriceEl.textContent);

        // Update Clearpay on price change (Klarna stays static)
        updateAfterpayDebounced();
      });
    };

    state.priceObserver = new MutationObserver(updatePrice);
    state.priceObserver.observe(realPrice, {
      childList: true,
      characterData: true,
      subtree: true
    });

    return true;
  }

  /* -------------------------------------------
   * Klarna (static base price — never updates)
   * ----------------------------------------- */
  function initKlarnaPlacement() {
    if (!CONFIG.ENABLE_KLARNA || state.klarnaInitialized) return false;

    const nodes = document.querySelectorAll(CONFIG.KLARNA_FIND_SEL);
    if (!nodes.length || !window.KlarnaOnsiteService) return false;

    state.klarnaInitialized = true;

    // Set base price once and never update
    if (!state.klarnaBasePence) {
      const pounds = getPriceFromCPBState() ?? getPriceFromDOMFallback();
      const pence = poundsToPenceInt(pounds);

      if (pence) {
        state.klarnaBasePence = pence;
        nodes.forEach(node => {
          node.setAttribute('data-purchase-amount', String(pence));
        });
      }
    }

    // Move into our container
    if (!state.movedKlarnaOnce && state.klarnaWrap) {
      state.klarnaWrap.append(...nodes);
      state.movedKlarnaOnce = true;
    }

    // Refresh
    utils.setTrackedTimeout(() => {
      if (window.KlarnaOnsiteService?.push) {
        window.KlarnaOnsiteService.push({ eventName: 'refresh-placements' });
      }
    }, 100);

    return true;
  }

  function hookKlarnaAsyncCallback() {
    const prev = window.klarnaAsyncCallback;
    if (prev && prev.__cpbWrapped) return;

    window.klarnaAsyncCallback = function () {
      try { if (typeof prev === 'function') prev(); } catch (_) {}

      utils.setTrackedTimeout(() => {
        state.klarnaInitialized = false;
        initKlarnaPlacement();
      }, 100);
    };
    window.klarnaAsyncCallback.__cpbWrapped = true;
  }

  /* -------------------------------------------
   * Clearpay / Afterpay (updates dynamically)
   * ----------------------------------------- */
  function findAfterpayTemplate() {
    return utils.qs('[data-cpb-afterpay-placement]')
      || utils.qs('[data-afterpay-product-placement]');
  }

  function buildAfterpayPlacement(amountValue) {
    const template = findAfterpayTemplate();
    let placement = null;

    if (template && template.tagName && template.tagName.toLowerCase() === 'square-placement') {
      placement = template.cloneNode(true);
    } else {
      placement = document.createElement('square-placement');
      placement.setAttribute('data-mpid', '82c70122-b711-43ac-80a9-d3edde26ee88');
      placement.setAttribute('data-placement-id', '89eb413b-d599-4104-aa59-c47c2f3c75b0');
      placement.setAttribute('data-page-type', 'product');
      placement.setAttribute('data-currency', 'GBP');
      placement.setAttribute('data-consumer-locale', 'en-GB');
      placement.setAttribute('data-is-eligible', 'true');
    }

    placement.removeAttribute('data-cpb-afterpay-placement');
    placement.removeAttribute('data-afterpay-product-placement');
    placement.setAttribute('data-amount', amountValue);

    return placement;
  }

  function updateAfterpay() {
    if (!CONFIG.ENABLE_AFTERPAY || !state.afterpayWrap || state.updatingAfterpay) return;

    state.updatingAfterpay = true;
    try {
      const pounds = getPriceFromCPBState() ?? getPriceFromDOMFallback();
      const amount = Number(pounds);
      if (!Number.isFinite(amount) || amount <= 0) return;

      const formattedAmount = amount.toFixed(2);
      const existingPlacement = state.afterpayWrap.querySelector('square-placement');
      if (state.lastAfterpayAmount === formattedAmount && existingPlacement) return;

      const placement = buildAfterpayPlacement(formattedAmount);
      state.afterpayWrap.replaceChildren(placement);
      state.lastAfterpayAmount = formattedAmount;
    } finally {
      state.updatingAfterpay = false;
    }
  }

  function updateAfterpayDebounced() {
    if (state.afterpayDebounceTimer) clearTimeout(state.afterpayDebounceTimer);
    state.afterpayDebounceTimer = setTimeout(() => {
      state.afterpayDebounceTimer = null;
      updateAfterpay();
    }, CONFIG.AFTERPAY_DEBOUNCE);
  }

  /* -------------------------------------------
   * Description move
   * ----------------------------------------- */
  function moveDescriptionOnce(mountId) {
    const description = utils.qs('.product-block--description');
    const cpbDescription = utils.qs('.cpb-product-description');

    if (!description || !cpbDescription) return false;

    const move = () => {
      if (state.mountedId !== mountId) return;
      cpbDescription.replaceChildren(description);
    };

    const personalizeButton = utils.qs('.personalize-it--js');
    if (personalizeButton) {
      personalizeButton.addEventListener('click', move, { once: true });
    } else {
      move();
    }

    return true;
  }

  /* -------------------------------------------
   * DOM placement
   * ----------------------------------------- */
  function ensureKlarnaWrap() {
    if (!CONFIG.ENABLE_KLARNA || state.klarnaWrap) return;
    state.klarnaWrap = document.createElement('div');
    state.klarnaWrap.className = 'cpb-klarna-container';
  }

  function ensureAfterpayWrap() {
    if (!CONFIG.ENABLE_AFTERPAY || state.afterpayWrap) return;
    state.afterpayWrap = document.createElement('div');
    state.afterpayWrap.className = 'cpb-afterpay-container';
  }

  function placeElements(mountId) {
    if (state.mountedId !== mountId) return false;

    const productTitle = utils.qs('.cpb-product-title', state.panelsContainer);
    if (!productTitle) return false;

    const ratingStars = utils.qs('.product__information .klaviyo-star-rating-widget');

    ensureCustomPriceContainer();
    ensureKlarnaWrap();
    ensureAfterpayWrap();

    // Stars after title
    if (ratingStars && ratingStars.parentElement !== productTitle.parentElement) {
      productTitle.insertAdjacentElement('afterend', ratingStars);
    }

    // Price after stars (or title)
    const priceTarget = ratingStars || productTitle;
    if (state.customPricesContainer && !state.customPricesContainer.isConnected) {
      priceTarget.insertAdjacentElement('afterend', state.customPricesContainer);
    }

    // Klarna after price
    let lastEl = state.customPricesContainer || priceTarget;
    if (state.klarnaWrap && !state.klarnaWrap.isConnected) {
      lastEl.insertAdjacentElement('afterend', state.klarnaWrap);
      lastEl = state.klarnaWrap;
    }

    // Clearpay after Klarna
    if (state.afterpayWrap && !state.afterpayWrap.isConnected) {
      lastEl.insertAdjacentElement('afterend', state.afterpayWrap);
    }

    return true;
  }

  /* -------------------------------------------
   * Inject CSS (hides original price)
   * ----------------------------------------- */
  function injectCSSOnce() {
    if (document.getElementById('cpb-payments-layout')) return;

    const style = document.createElement('style');
    style.id = 'cpb-payments-layout';
    style.textContent = `
      #product-builder .cpb-panels-container .cpb-product-price {
        display: none !important;
      }
    `;
    document.head.appendChild(style);
  }

  /* -------------------------------------------
   * Navigation button removal (JS backup)
   * ----------------------------------------- */
  const NAV_REGEX = /PREVIOUS|NEXT|RESET|PREVIOUS TAB|NEXT TAB/i;

  function removeNavigationButtons() {
    const buttons = utils.qsa('.cpb-product-actions button, .cpb-product-actions .cpb-actions-btn');
    if (!buttons.length) return false;

    let removed = 0;
    buttons.forEach(btn => {
      if (NAV_REGEX.test((btn.textContent || '').trim())) {
        btn.remove();
        removed++;
      }
    });

    return removed > 0;
  }

  /* -------------------------------------------
   * Out of stock overlays
   * ----------------------------------------- */
  function updateOutOfStockOverlays() {
    const wrappers = utils.qsa('.out-stock-wrapper');
    if (!wrappers.length) return false;

    wrappers.forEach(wrapper => {
      const overlay = utils.qs('.out-stock-overlay span', wrapper);
      const img = utils.qs('img', wrapper);
      if (!overlay || !img) return;

      const title = (img.alt || 'Product')
        .replace(/\s*\([^)]*\)\s*$/g, '')
        .trim();

      overlay.textContent = `${title} - OUT OF STOCK`;
    });

    return true;
  }

  /* -------------------------------------------
   * Init
   * ----------------------------------------- */
  function init() {
    cleanup();
    state.mountedId = utils.newMountId();
    const mountId = state.mountedId;

    if (!initGlobals()) {
      utils.error('Product builder not found');
      return;
    }

    injectCSSOnce();
    window.PXUTheme.contentCreator.accordion?.init?.();

    ensureCustomPriceContainer();

    utils.retry(() => moveDescriptionOnce(mountId));
    utils.retry(() => placeElements(mountId));

    // Navigation button removal (backup to CSS hide)
    utils.retry(removeNavigationButtons);

    // Klarna init (static, retried until Klarna SDK loads)
    if (CONFIG.ENABLE_KLARNA) {
      hookKlarnaAsyncCallback();
      utils.retry(initKlarnaPlacement);
      utils.setTrackedTimeout(() => initKlarnaPlacement(), 1200);
      utils.setTrackedTimeout(() => initKlarnaPlacement(), 2500);
    }

    // Clearpay init
    if (CONFIG.ENABLE_AFTERPAY) {
      utils.retry(updateAfterpayDebounced);
    }

    // Out of stock overlays
    CONFIG.OOS_RETRY_DELAYS_MS.forEach(delay => {
      utils.setTrackedTimeout(() => updateOutOfStockOverlays(), delay);
    });
  }

  // Listen for CPB events
  document.body.addEventListener('CPB_ON_PRODUCT_MOUNTED', init);

  document.body.addEventListener('CPB_ON_OPTIONS_UPDATE', () => {
    // Only update Clearpay on option change (Klarna stays static)
    updateAfterpayDebounced();
  });

  window.addEventListener('pagehide', cleanup);
  window.addEventListener('beforeunload', cleanup);
})();
